import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { NotificationVisitService } from 'src/app/services/notification-visit.service';
import { AuthState } from 'src/app/store/auth/auth.state';

/**
 * Service that manages persistence for Navattic tutorial relocation features:
 * - "New" badge dismissal for menu items
 * - Callout dismissal
 */
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class NavatticPersistenceService {
  private readonly takeTourNewDismissedKey = 'navattic_take_tour_new_dismissed';
  private readonly bookCallNewDismissedKey = 'navattic_book_call_new_dismissed';
  private readonly tutorialsMovedCalloutId = 'navattic-tutorials-moved-callout';
  private readonly bookCallMovedCalloutId = 'navattic-book-call-moved-callout';

  private readonly takeTourNewDismissed$ = new BehaviorSubject<boolean>(
    this.isDismissed(this.takeTourNewDismissedKey),
  );
  private readonly bookCallNewDismissed$ = new BehaviorSubject<boolean>(
    this.isDismissed(this.bookCallNewDismissedKey),
  );
  private readonly calloutDismissed$ = new BehaviorSubject<boolean>(
    this.isCalloutDismissed(this.tutorialsMovedCalloutId),
  );
  private readonly bookCallMovedCalloutDismissed$ = new BehaviorSubject<boolean>(
    this.isCalloutDismissed(this.bookCallMovedCalloutId),
  );

  constructor(
    private readonly notificationVisitService: NotificationVisitService,
    private readonly store: Store,
  ) {
    this.refreshFromStorage();
    this.store.select(AuthState.user).pipe(
      untilDestroyed(this),
    ).subscribe(() => {
      this.refreshFromStorage();
    });
  }

  private refreshFromStorage(): void {
    this.takeTourNewDismissed$.next(this.isDismissed(this.takeTourNewDismissedKey));
    this.bookCallNewDismissed$.next(this.isDismissed(this.bookCallNewDismissedKey));
    this.calloutDismissed$.next(this.isCalloutDismissed(this.tutorialsMovedCalloutId));
    this.bookCallMovedCalloutDismissed$.next(this.isCalloutDismissed(this.bookCallMovedCalloutId));
  }

  /**
   * Checks if a notification has been dismissed (visited).
   */
  private isDismissed(key: string): boolean {
    return this.notificationVisitService.visitedNotification(key);
  }

  private isCalloutDismissed(calloutId: string): boolean {
    return this.notificationVisitService.visitedNotification(this.getCalloutStorageKey(calloutId));
  }

  private getCalloutStorageKey(calloutId: string): string {
    return `callout${calloutId}`;
  }

  /**
   * Observable that emits true if the "Take a Tour" new badge has been dismissed.
   */
  isTakeTourNewDismissed$(): Observable<boolean> {
    return this.takeTourNewDismissed$.pipe(distinctUntilChanged());
  }

  /**
   * Observable that emits true if the "Book a Call" new badge has been dismissed.
   */
  isBookCallNewDismissed$(): Observable<boolean> {
    return this.bookCallNewDismissed$.pipe(distinctUntilChanged());
  }

  /**
   * Observable that emits true if the "Tutorials moved" callout has been dismissed.
   */
  isCalloutDismissed$(): Observable<boolean> {
    return this.calloutDismissed$.pipe(distinctUntilChanged());
  }

  /**
   * Observable that emits true if the "Book a Call moved" callout has been dismissed.
   */
  isBookCallMovedCalloutDismissed$(): Observable<boolean> {
    return this.bookCallMovedCalloutDismissed$.pipe(distinctUntilChanged());
  }

  /**
   * Observable that emits true if the "Take a Tour" new badge should be shown.
   */
  showTakeTourNewBadge$(): Observable<boolean> {
    return this.isTakeTourNewDismissed$().pipe(
      map(dismissed => !dismissed),
      distinctUntilChanged(),
    );
  }

  /**
   * Observable that emits true if the "Book a Call" new badge should be shown.
   */
  showBookCallNewBadge$(): Observable<boolean> {
    return this.isBookCallNewDismissed$().pipe(
      map(dismissed => !dismissed),
      distinctUntilChanged(),
    );
  }

  /**
   * Marks the "Take a Tour" new badge as dismissed.
   * Called when the menu item is clicked.
   */
  dismissTakeTourNewBadge(): void {
    this.notificationVisitService.setNotificationVisit(this.takeTourNewDismissedKey);
    this.takeTourNewDismissed$.next(true);
  }

  /**
   * Marks the "Book a Call" new badge as dismissed.
   * Called when the menu item is clicked.
   */
  dismissBookCallNewBadge(): void {
    this.notificationVisitService.setNotificationVisit(this.bookCallNewDismissedKey);
    this.bookCallNewDismissed$.next(true);
  }

  /**
   * Marks the "Tutorials moved" callout as dismissed.
   * Called when "Got it" is clicked.
   */
  dismissCallout(): void {
    this.notificationVisitService.setNotificationVisit(this.getCalloutStorageKey(this.tutorialsMovedCalloutId));
    this.calloutDismissed$.next(true);
  }

  /**
   * Marks the "Book a Call moved" callout as dismissed.
   * Called when "Got it" is clicked.
   */
  dismissBookCallMovedCallout(): void {
    this.notificationVisitService.setNotificationVisit(this.getCalloutStorageKey(this.bookCallMovedCalloutId));
    this.bookCallMovedCalloutDismissed$.next(true);
  }
}
